import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { MonitoringService, MonitoringFinding } from '../../core/services/monitoring.service';
import { EnrichmentService, EnrichmentView, EvidenceSummary } from '../../core/services/enrichment.service';

/**
 * First-class, read-only finding detail surface (route: /findings/:findingId).
 *
 * A canonical, deep-linkable place to see one finding's diagnosis + evidence —
 * complementing the inline activity-feed expansion and the ticket drawers, all of
 * which reuse the same `EnrichmentService` + `DiagnosisSectionComponent`.
 *
 * Fully additive and non-blocking: finding metadata and enrichment load
 * independently, and either failing degrades to an honest empty state rather than
 * blocking the page. No new backend endpoint — finding metadata comes from the
 * existing findings list; diagnosis/evidence from the enrichment API by id.
 */
@Component({
  selector: 'app-finding-detail',
  templateUrl: './finding-detail.component.html',
})
export class FindingDetailComponent implements OnInit, OnDestroy {
  findingId = '';

  finding:        MonitoringFinding | null = null;
  findingLoading = true;
  findingMissing = false;

  enrichment:        EnrichmentView | null = null;
  evidence:          EvidenceSummary | null = null;
  enrichmentLoading  = false;

  private routeSub?:      Subscription;
  private findingSub?:    Subscription;
  private enrichmentSub?: Subscription;

  constructor(
    private readonly route:       ActivatedRoute,
    private readonly monitoring$: MonitoringService,
    private readonly enrichment$: EnrichmentService,
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe(pm => {
      const id = pm.get('findingId');
      if (!id) return;
      this.findingId = id;
      this.loadFinding(id);
      this.loadEnrichment(id);
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.findingSub?.unsubscribe();
    this.enrichmentSub?.unsubscribe();
  }

  /** Finding metadata from the existing findings list (client-side select). */
  private loadFinding(id: string): void {
    this.findingLoading = true;
    this.findingMissing = false;
    this.finding        = null;
    this.findingSub?.unsubscribe();
    this.findingSub = this.monitoring$.getFindings().subscribe({
      next: (findings) => {
        this.finding        = findings.find(f => f.findingId === id) ?? null;
        this.findingMissing = !this.finding;
        this.findingLoading = false;
      },
      error: () => { this.findingLoading = false; this.findingMissing = true; },
    });
  }

  /** Diagnosis + evidence; isolated so a failure never blocks the page. */
  private loadEnrichment(id: string): void {
    this.enrichment        = null;
    this.evidence          = null;
    this.enrichmentLoading = true;
    this.enrichmentSub?.unsubscribe();
    this.enrichmentSub = this.enrichment$.getEnrichment(id, { includeEvidence: true }).subscribe({
      next: ({ enrichment, evidence }) => {
        this.enrichment        = enrichment;
        this.evidence          = evidence ?? null;
        this.enrichmentLoading = false;
      },
      error: () => {
        this.enrichment        = null;
        this.evidence          = null;
        this.enrichmentLoading = false;
      },
    });
  }

  // ── Presentation helpers ──────────────────────────────────────────────────────

  severityBadgeClass(sev: string | undefined): string {
    switch (sev) {
      case 'CRITICAL': return 'bg-red-100 text-red-700';
      case 'HIGH':     return 'bg-orange-100 text-orange-700';
      case 'MEDIUM':   return 'bg-amber-100 text-amber-700';
      case 'LOW':      return 'bg-blue-100 text-blue-700';
      default:         return 'bg-slate-100 text-slate-600';
    }
  }

  serviceTypeLabel(t: string | undefined): string {
    if (!t) return '—';
    return t.charAt(0) + t.slice(1).toLowerCase().replace(/_/g, ' ');
  }

  /** Duration of the evidence collection window, in minutes. */
  get windowMinutes(): number | null {
    if (!this.evidence) return null;
    return Math.max(0, Math.round((this.evidence.window.endMs - this.evidence.window.startMs) / 60000));
  }
}
